import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, Typography, Box, CardActionArea, Button, IconButton, Snackbar, Alert, Skeleton, Tooltip } from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import ArchiveIcon from '@mui/icons-material/Archive';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from './config';
import { useTranslation } from 'react-i18next';

const ArchivedTripsPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [trips, setTrips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchArchivedTrips = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/trips/get.php?status=archived`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setTrips(data);
            } else {
                setError(t('fetch_archived_trips_error'));
            }
        } catch (err) {
            setError(t('fetch_archived_trips_general_error'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchArchivedTrips();
    }, [fetchArchivedTrips]);

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {[1, 2].map(i => (
                        <Card key={i} sx={{ minWidth: 275, flexGrow: 1, maxWidth: 345, opacity: 0.6 }}>
                            <CardContent>
                                <Skeleton variant="text" width="80%" height={32} />
                                <Skeleton variant="text" width="60%" />
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    const handleTripClick = (tripId) => {
        navigate(`/trip/${tripId}`);
    };

    const handleRestore = async (e, tripId) => {
        e.stopPropagation(); // Prevent card click
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/trips/restore.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ trip_id: tripId })
            });

            if (response.ok) {
                setTrips(trips.filter(trip => trip.trip_id !== tripId));
                setSnackbar({ open: true, message: t('trip_restored_success') || 'Trip restored successfully', severity: 'success' });
            } else {
                setSnackbar({ open: true, message: t('trip_restore_error') || 'Failed to restore trip', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: t('trip_restore_error') || 'Failed to restore trip', severity: 'error' });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ArchiveIcon sx={{ color: 'text.secondary' }} />
                {t('archived_trips_title')}
            </Typography>
            
            {trips.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {trips.map(trip => (
                        <Card 
                            key={trip.trip_id} 
                            sx={{ 
                                minWidth: 275, 
                                flexGrow: 1, 
                                maxWidth: 345,
                                opacity: 0.85,
                                bgcolor: 'grey.50',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    opacity: 1,
                                    transform: 'translateY(-2px)',
                                }
                            }}
                        >
                            <CardActionArea onClick={() => handleTripClick(trip.trip_id)}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Typography variant="h6" component="div" fontWeight="bold" sx={{ color: 'text.secondary' }}>
                                            {trip.name}
                                        </Typography>
                                        <Tooltip title={t('restore_trip') || 'Restore trip'}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleRestore(e, trip.trip_id)}
                                                sx={{ 
                                                    color: 'success.main',
                                                    bgcolor: 'success.50',
                                                    '&:hover': { 
                                                        bgcolor: 'success.100',
                                                        transform: 'scale(1.1)',
                                                    },
                                                    transition: 'all 0.2s ease',
                                                }}
                                            >
                                                <RestoreIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                        <Typography variant="body2" color="text.disabled">
                                            {trip.start_date && trip.end_date
                                                ? `${trip.start_date} - ${trip.end_date}`
                                                : `${new Date(0, trip.month_of_year - 1).toLocaleString('default', { month: 'long' })} (${trip.flexible_duration_type})`
                                            }
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    ))}
                </Box>
            ) : (
                <Box sx={{ 
                    textAlign: 'center', 
                    py: 8,
                    px: 2,
                }}>
                    <Box sx={{
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px',
                    }}>
                        <ArchiveIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {t('no_archived_trips_found')}
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                        {t('archived_trips_hint') || 'Archived trips will appear here'}
                    </Typography>
                </Box>
            )}
            
            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={4000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ArchivedTripsPage;